/* cs-buybox.js — pack selection, per-unit variant rows, add-ons, one total.
 *
 * Horizon port of the store's own buy-box script. The pack/row/add-on logic is
 * unchanged; what changed is how it talks to the theme:
 *   - the price row is our own cs-price block, not the theme price snippet
 *   - the button label is written into Horizon's add-to-cart button, keeping
 *     its "added" animation span intact
 *   - the add itself goes to /cart/add.js with every selected row as its own
 *     line, then a CartLinesUpdateEvent carries the rendered cart sections so
 *     the drawer and the cart icon update the way they do for a normal add
 *   - Horizon's sticky bar clicks the main button, so one interceptor covers both
 *
 * All state lives in the DOM (the checked radio, data-checked on add-on cards),
 * so two instances on one page cannot fight over a shared variable.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const HOST = '[data-cs-packs]';

/* ---------------------------------------------------------------- instance -- */

function instanceFor(host) {
  if (!host) return null;
  const raw = host.querySelector('[data-cs-packs-config]');
  if (!raw) return null;
  let config;
  try {
    config = JSON.parse(raw.textContent);
  } catch (e) {
    return null;
  }
  return {
    host,
    scope: host.closest('.shopify-section') || document,
    config,
    copy: config.copy || {},
    swatches: config.swatches || {},
    rows: host.querySelector('[data-cs-rows]'),
  };
}

const instanceFromEvent = (target) =>
  target && target.closest ? instanceFor(target.closest(HOST)) : null;

/* ------------------------------------------------------------------- money -- */

// Writes only on change. The MutationObserver below re-renders on any DOM
// change, so an unconditional write would feed itself every frame.
function setText(node, text) {
  if (node && node.textContent !== text) node.textContent = text;
}

const money = (minor) => Math.round(minor / 100).toLocaleString('tr-TR');

function fill(template, values) {
  let out = String(template || '');
  Object.keys(values).forEach((key) => {
    out = out.split('[' + key + ']').join(values[key]);
  });
  return out;
}

/* ----------------------------------------------------------------- pricing -- */

const options = (inst) => inst.config.variantOptions || [];

const variantFor = (inst, colour, size) =>
  options(inst).find((v) => v.option1 === colour && v.option2 === size) || null;

function priceFor(inst, colour, size) {
  const match = variantFor(inst, colour, size);
  return match && typeof match.price === 'number' ? match.price : 0;
}

// The dearest colour is the reference; anything under it is on offer.
function basePrice(inst) {
  const prices = options(inst).map((v) => (typeof v.price === 'number' ? v.price : 0));
  return prices.length ? Math.max(...prices) : 0;
}

function colourSaving(inst, colour) {
  const prices = options(inst)
    .filter((v) => v.option1 === colour && typeof v.price === 'number')
    .map((v) => v.price);
  if (!prices.length) return 0;
  return Math.max(0, basePrice(inst) - Math.min(...prices));
}

function rowSelections(inst) {
  const rows = inst.rows ? [...inst.rows.querySelectorAll('[data-cs-row]')] : [];
  return rows.map((row) => ({
    colour: row.querySelector('[data-cs-colour]')?.value || '',
    size: row.querySelector('[data-cs-size]')?.value || '',
  }));
}

/* ------------------------------------------------------------ availability -- */

// Sold-out combinations are disabled in the native selects, which greys them in
// the open list. Stock is shared across a pack's rows: one Pembe/S in stock can
// fill one row, not two. "stock" is null when Shopify does not limit the variant.
const isSellable = (v) => !!v && v.available !== false;
const stockOf = (v) => (v && typeof v.stock === 'number' ? v.stock : Infinity);

// "upTo" limits the count to the rows above, which have already been settled
function remaining(inst, colour, size, picks, skip, upTo = picks.length) {
  const v = variantFor(inst, colour, size);
  if (!isSellable(v)) return 0;
  const used = picks.slice(0, upTo).filter((p, i) => i !== skip && p.colour === colour && p.size === size).length;
  return stockOf(v) - used;
}

const totalStock = (inst) =>
  options(inst).reduce((sum, v) => sum + (isSellable(v) ? stockOf(v) : 0), 0);

function setOption(option, label, disabled) {
  if (option.disabled !== disabled) option.disabled = disabled;
  if (option.textContent !== label) setText(option, label);
}

// Rows are settled top to bottom, so an upper row keeps its pick and a lower
// row gives way: a row whose pick is gone moves to the first size of its colour
// that is left, then to the first colour that has any. The disabled flags are
// set afterwards against every other row.
function renderAvailability(inst, quantity) {
  if (!inst.rows) return;
  const rows = [...inst.rows.querySelectorAll('[data-cs-row]')].slice(0, quantity);
  const picks = rows.map((row) => ({
    colour: row.querySelector('[data-cs-colour]').value,
    size: row.querySelector('[data-cs-size]').value,
  }));
  const soldOut = inst.copy.soldOut || 'Tükendi';

  rows.forEach((row, i) => {
    const colourSelect = row.querySelector('[data-cs-colour]');
    const sizeSelect = row.querySelector('[data-cs-size]');
    const sizes = [...sizeSelect.options].map((o) => o.value);
    const left = (c, s) => remaining(inst, c, s, picks, i, i) > 0;

    if (!sizes.some((s) => left(colourSelect.value, s))) {
      const next = [...colourSelect.options].find((o) => sizes.some((s) => left(o.value, s)));
      if (next) colourSelect.value = next.value;
    }
    if (!left(colourSelect.value, sizeSelect.value)) {
      const next = sizes.find((s) => left(colourSelect.value, s));
      if (next) sizeSelect.value = next;
    }
    picks[i] = { colour: colourSelect.value, size: sizeSelect.value };
  });

  rows.forEach((row, i) => {
    const colourSelect = row.querySelector('[data-cs-colour]');
    const sizeSelect = row.querySelector('[data-cs-size]');
    const sizes = [...sizeSelect.options].map((o) => o.value);
    const colour = colourSelect.value;
    const colourLeft = (c) => sizes.some((s) => remaining(inst, c, s, picks, i) > 0);

    // the colour list keeps its own labels (renderSwatches); only the flag is set here
    [...colourSelect.options].forEach((o) => {
      const off = !colourLeft(o.value);
      if (o.disabled !== off) o.disabled = off;
    });
    [...sizeSelect.options].forEach((o) => {
      const off = remaining(inst, colour, o.value, picks, i) <= 0;
      setOption(o, off ? o.value + ' · ' + soldOut : o.value, off);
    });
  });
}

/* ------------------------------------------------------------------- state -- */

const packs = (inst) => [...inst.host.querySelectorAll('[data-cs-pack]')];
const addons = (inst) => [...inst.host.querySelectorAll('[data-cs-addon]')];

function selectedPack(inst) {
  const stock = totalStock(inst);
  const all = packs(inst);
  all.forEach((pack) => {
    const out = (parseInt(pack.dataset.qty, 10) || 1) > stock;
    pack.classList.toggle('cs-pack--soldout', out);
    pack.setAttribute('aria-disabled', out ? 'true' : 'false');
    pack.querySelector('.cs-pack__input').disabled = out;
  });
  const open = all.filter((pack) => !pack.classList.contains('cs-pack--soldout'));
  return open.find((pack) => pack.querySelector('.cs-pack__input').checked) || open[0] || all[0];
}

function packState(pack, inst) {
  const compare = parseInt(pack.dataset.compare, 10) || 0;
  const quantity = parseInt(pack.dataset.qty, 10) || 1;
  const discount = parseInt(pack.dataset.discount, 10) || 0;
  let total = parseInt(pack.dataset.total, 10) || 0;
  let unit = parseInt(pack.dataset.unit, 10) || 0;
  let colourSaved = 0;

  // every row is priced or none is: a half-computed total is worse than the
  // schema figure it replaces
  const rows = inst ? rowSelections(inst).slice(0, quantity) : [];
  const priced = rows.length === quantity && rows.every((r) => priceFor(inst, r.colour, r.size) > 0);

  if (priced) {
    let sum = 0;
    rows.forEach((r) => {
      sum += priceFor(inst, r.colour, r.size);
      colourSaved += colourSaving(inst, r.colour);
    });
    total = sum - discount;
    unit = Math.round(total / quantity);
  }

  return {
    node: pack,
    quantity,
    total,
    unit,
    compare,
    saved: compare - total,
    colourSaved,
    percent: compare ? Math.round((1 - total / compare) * 100) : 0,
  };
}

function isFree(addon, quantity) {
  const freeFrom = parseInt(addon.dataset.freeFrom, 10) || 0;
  return freeFrom > 0 && quantity >= freeFrom;
}

const isTicked = (addon) => addon.getAttribute('data-checked') === 'true';

/* ------------------------------------------------------------------ render -- */

function renderPacks(inst, state) {
  packs(inst).forEach((pack) => {
    const selected = pack === state.node;
    pack.classList.toggle('cs-pack--selected', selected);
    pack.querySelector('.cs-pack__input').checked = selected;

    const each = packState(pack, inst);
    const sub = pack.querySelector('.cs-pack__sub');
    if (sub) {
      if (sub.dataset.template === undefined) sub.dataset.template = sub.textContent;
      const savingPhrase =
        each.quantity > 1 && each.saved > 0
          ? '<span class="cs-pack__saved">' + fill(inst.copy.savingLine, { tutar: money(each.saved) }) + '</span>'
          : '';
      const text = fill(sub.dataset.template, { kazanc: savingPhrase });
      if (sub.innerHTML !== text) sub.innerHTML = text;
      sub.hidden = text.trim() === '';
    }

    const totalNode = pack.querySelector('.cs-pack__total');
    setText(totalNode, money(each.total) + ' TL');
    totalNode.classList.toggle('cs-pack__total--sale', each.colourSaved > 0);
    setText(pack.querySelector('.cs-pack__compare'), money(each.compare) + ' TL');
  });

  // the rows element is moved, not rebuilt, so the selects keep their values
  const slot = state.node.querySelector('[data-cs-rows-slot]');
  if (slot && inst.rows && inst.rows.parentNode !== slot) slot.appendChild(inst.rows);
  if (inst.rows) inst.rows.hidden = false;

  (inst.rows ? [...inst.rows.querySelectorAll('[data-cs-row]')] : []).forEach((row, index) => {
    row.hidden = index >= state.quantity;
    row.querySelector('.cs-row__label').hidden = state.quantity < 2;
  });
}

function renderHeaderPrice(inst, state) {
  inst.scope.querySelectorAll('[data-cs-price-now]').forEach((n) => {
    setText(n, money(state.total) + ' TL');
  });
  inst.scope.querySelectorAll('[data-cs-price-was]').forEach((n) => {
    setText(n, money(state.compare) + ' TL');
    n.hidden = state.compare <= state.total;
  });
  inst.scope.querySelectorAll('[data-cs-price-badge]').forEach((n) => {
    setText(n, fill(inst.copy.discountBadge, { yuzde: state.percent }));
    n.hidden = state.percent <= 0;
  });
  // Horizon's sticky bar prints the variant price; it follows the pack total too
  inst.scope.querySelectorAll('.sticky-add-to-cart__price').forEach((n) => {
    setText(n, money(state.total) + ' TL');
  });
}

function renderAddons(inst, state) {
  addons(inst).forEach((addon) => {
    const price = parseInt(addon.dataset.price, 10) || 0;
    const free = isFree(addon, state.quantity);

    // one that comes with the pack is not a control; one that costs money is
    // never pre-ticked
    if (free) addon.setAttribute('data-checked', 'false');
    const ticked = !free && isTicked(addon);

    addon.classList.toggle('cs-addon--free', free);
    addon.classList.toggle('cs-addon--checked', ticked);
    addon.setAttribute('aria-checked', free || ticked ? 'true' : 'false');
    addon.setAttribute('aria-disabled', free ? 'true' : 'false');
    addon.tabIndex = free ? -1 : 0;

    const amount = addon.querySelector('[data-cs-addon-amount]');
    amount.hidden = free;
    setText(amount, money(price) + ' TL');
    addon.querySelector('[data-cs-addon-free]').hidden = !free;

    const was = addon.querySelector('[data-cs-addon-was]');
    was.hidden = !free;
    setText(was, money(price) + ' TL');

    const tag = addon.querySelector('[data-cs-addon-tag]');
    const freeFrom = parseInt(addon.dataset.freeFrom, 10) || 0;
    const label = free ? fill(inst.copy.includedTag, { adet: freeFrom }) : addon.dataset.tagOptional || '';
    setText(tag, label);
    tag.hidden = label === '';
    tag.classList.toggle('cs-chip--terra', !free && label !== '');
    tag.classList.toggle('cs-chip--sage', free);
  });
}

const BUTTONS = 'add-to-cart-component button[name="add"], .sticky-add-to-cart__button';

function renderButton(inst, state) {
  let total = state.total;
  addons(inst).forEach((addon) => {
    if (!isFree(addon, state.quantity) && isTicked(addon)) total += parseInt(addon.dataset.price, 10) || 0;
  });

  const label = fill(inst.copy.addToCart, { tutar: money(total) });
  inst.scope.querySelectorAll(BUTTONS).forEach((button) => {
    let slot = button.querySelector('.cs-atc-label');
    if (!slot) {
      // drop the theme's bare text, keep its icon and "added" animation spans
      [...button.childNodes].forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) node.remove();
      });
      slot = document.createElement('span');
      slot.className = 'cs-atc-label';
      const anchor = button.querySelector('.add-to-cart__added');
      button.insertBefore(slot, anchor || null);
    }
    if (slot.textContent !== label) setText(slot, label);
  });
}

function renderSwatches(inst) {
  if (!inst.rows) return;
  inst.rows.querySelectorAll('[data-cs-colour]').forEach((select) => {
    const field = select.closest('[data-cs-colour-field]');
    const dot = field && field.querySelector('[data-cs-dot]');
    if (!dot) return;
    const hex = inst.swatches[(select.value || '').trim()];
    dot.style.backgroundColor = hex || 'transparent';
    dot.hidden = !hex;

    // the offer is named in the list, never on the selected option, which the
    // closed native select already shows as its own text
    [...select.options].forEach((option) => {
      const saving = option.selected || option.disabled ? 0 : colourSaving(inst, option.value);
      const label = option.disabled
        ? option.value + ' · ' + (inst.copy.soldOut || 'Tükendi')
        : saving > 0
          ? fill(inst.copy.colourSaleOption, { renk: option.value, tutar: money(saving) })
          : option.value;
      if (option.textContent !== label) setText(option, label);
    });

    const tag = field.querySelector('[data-cs-colour-tag]');
    if (tag) {
      const selected = colourSaving(inst, select.value);
      const text = selected > 0 ? fill(inst.copy.colourSaleTag, { tutar: money(selected) }) : '';
      if (tag.textContent !== text) setText(tag, text);
      tag.hidden = selected <= 0;
    }
  });
}

function render(inst) {
  if (!inst) return;
  const pack = selectedPack(inst);
  if (!pack) return;
  renderAvailability(inst, parseInt(pack.dataset.qty, 10) || 1);
  const state = packState(pack, inst);
  renderPacks(inst, state);
  renderHeaderPrice(inst, state);
  renderAddons(inst, state);
  renderSwatches(inst);
  renderButton(inst, state);
}

function renderAll() {
  document.querySelectorAll(HOST).forEach((host) => {
    const inst = instanceFor(host);
    if (!inst) return;
    if (host.dataset.csReady !== '1') {
      addons(inst).forEach((addon) => addon.setAttribute('data-checked', 'false'));
      host.dataset.csReady = '1';
    }
    render(inst);
  });
}

/* ------------------------------------------------------------------ events -- */

function onClick(event) {
  if (event.target.closest('select')) return;
  const inst = instanceFromEvent(event.target);
  if (!inst) return;

  if (event.target.closest('[data-cs-size-guide]')) {
    event.preventDefault();
    openSizeGuide(inst);
    return;
  }

  const pack = event.target.closest('[data-cs-pack]');
  if (pack) {
    if (pack.classList.contains('cs-pack--soldout')) return;
    pack.querySelector('.cs-pack__input').checked = true;
    render(inst);
    return;
  }

  const addon = event.target.closest('[data-cs-addon]');
  if (addon) {
    if (addon.getAttribute('aria-disabled') === 'true') return;
    addon.setAttribute('data-checked', isTicked(addon) ? 'false' : 'true');
    render(inst);
  }
}

function onKeydown(event) {
  if (event.key !== ' ' && event.key !== 'Enter') return;
  const inst = instanceFromEvent(event.target);
  if (!inst) return;
  const addon = event.target.closest('[data-cs-addon]');
  if (!addon || addon.getAttribute('aria-disabled') === 'true') return;
  event.preventDefault();
  addon.setAttribute('data-checked', isTicked(addon) ? 'false' : 'true');
  render(inst);
}

function onChange(event) {
  if (!event.target.matches || !event.target.matches('[data-cs-colour], [data-cs-size]')) return;
  render(instanceFromEvent(event.target));
}

// The size chart is the gallery image whose alt matches; scroll to it.
function openSizeGuide(inst) {
  const alt = (inst.copy.sizeGuideAlt || '').trim().toLocaleLowerCase('tr-TR');
  const images = [...inst.scope.querySelectorAll('.product-media img, media-gallery img, slideshow-slide img')];
  const target =
    images.find((img) => (img.getAttribute('alt') || '').trim().toLocaleLowerCase('tr-TR') === alt) ||
    images[images.length - 1];
  if (!target) return;
  const slide = target.closest('slideshow-slide');
  const slideshow = slide && slide.closest('slideshow-component');
  if (slideshow && typeof slideshow.select === 'function') {
    const index = [...slideshow.querySelectorAll('slideshow-slide')].indexOf(slide);
    if (index > -1) slideshow.select(index);
  }
  target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
}

/* ------------------------------------------------------------- add to cart -- */

const variantIdFor = (inst, colour, size) => variantFor(inst, colour, size)?.id || 0;

// every cart-items section on the page, so the drawer re-renders from the add
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

// Every selected row as its own line, plus the ticked or free add-ons. Shared
// by the theme add and the cash-on-delivery path, so both send the same lines.
async function cartItems(inst) {
  const state = packState(selectedPack(inst), inst);
  const bundleId = 'b_' + Date.now();
  const packLabel = state.quantity + ' adet';
  const items = [];

  (inst.rows ? [...inst.rows.querySelectorAll('[data-cs-row]')] : []).forEach((row, index) => {
    if (index >= state.quantity) return;
    const id = variantIdFor(inst, row.querySelector('[data-cs-colour]').value, row.querySelector('[data-cs-size]').value);
    if (!id) return;
    const properties = { _bundle_id: bundleId };
    if (state.quantity > 1) properties[inst.copy.packageProperty || 'Paket'] = packLabel;
    items.push({ id, quantity: 1, properties });
  });

  if (!items.length) throw new Error('no variant resolved for the selected rows');

  const inCart = {};
  try {
    const current = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    (current.items || []).forEach((line) => {
      inCart[line.id] = true;
    });
  } catch (e) {
    /* Shopify merges a duplicate line */
  }

  // a free add-on is still a real line: the BXGY discount is what zeroes it
  addons(inst).forEach((addon) => {
    const free = isFree(addon, state.quantity);
    if (!free && !isTicked(addon)) return;
    const id = parseInt(addon.dataset.variant, 10);
    if (!id || inCart[id]) return;
    items.push({ id, quantity: 1, properties: { _bundle_id: bundleId, _addon: free ? 'auto' : 'manual' } });
  });

  return items;
}

async function addToCart(inst, button) {
  const items = await cartItems(inst);
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({
        merchandiseId: 'gid://shopify/ProductVariant/' + item.id,
        quantity: item.quantity,
      })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const payload = await res.json();
    if (!res.ok) throw new Error(payload.description || 'cart/add failed');

    const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'cs-buybox',
        itemCount: items.length,
        sections: payload.sections,
        didError: false,
      },
    });
  } catch (error) {
    deferred.resolve({ cart: null, detail: { didError: true, source: 'cs-buybox' } });
    throw error;
  }
}

let submitting = false;

function onAddToCart(event) {
  const button = event.target.closest ? event.target.closest('add-to-cart-component button[name="add"]') : null;
  if (!button || submitting) return;
  if (button.dataset.csFallback === '1') return;

  const section = button.closest('.shopify-section');
  const inst = section ? instanceFor(section.querySelector(HOST)) : null;
  if (!inst) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  submitting = true;
  button.setAttribute('aria-busy', 'true');
  addToCart(inst, button)
    .catch((error) => {
      console.warn('[cs-buybox] falling back to the theme add to cart:', error);
      button.dataset.csFallback = '1';
      button.click();
      delete button.dataset.csFallback;
    })
    .finally(() => {
      submitting = false;
      button.removeAttribute('aria-busy');
    });
}

/* -------------------------------------------------------- cash on delivery -- */

// Releasit's product-page button orders the theme's own variant input, which
// the packs never touch, so it would order the first variant x1 whatever the
// rows say, without add-ons or the pack discount. Its cart form reads the real
// cart and applies the automatic discounts, so the product button puts the
// rows in the cart and reopens the form there.
const COD_BUTTON = 'button[class*="rsi_"]';
const COD_FLAG = 'cs_cod';

function onCodClick(event) {
  const button = event.target.closest ? event.target.closest(COD_BUTTON) : null;
  if (!button || button.closest('#cart-wrapper') || submitting) return;
  const section = button.closest('.shopify-section');
  const inst = section ? instanceFor(section.querySelector(HOST)) : null;
  if (!inst) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  submitting = true;
  button.setAttribute('aria-busy', 'true');
  cartItems(inst)
    .then((items) =>
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      })
    )
    .then((res) => {
      if (!res.ok) throw new Error('cart/add failed: ' + res.status);
      window.location.href = '/cart?' + COD_FLAG + '=1';
    })
    .catch((error) => {
      // let Releasit's own form open rather than leave the buyer stuck
      console.warn('[cs-buybox] COD via cart failed, opening the product form:', error);
      submitting = false;
      button.removeAttribute('aria-busy');
      button.dataset.csCodFallback = '1';
      button.click();
      delete button.dataset.csCodFallback;
    });
}

// On /cart?cs_cod=1, open Releasit's cart form once its button has rendered.
function openCartCodForm() {
  const params = new URLSearchParams(window.location.search);
  if (params.get(COD_FLAG) !== '1') return;
  params.delete(COD_FLAG);
  const rest = params.toString();
  history.replaceState(null, '', window.location.pathname + (rest ? '?' + rest : '') + window.location.hash);

  // The button renders before Releasit wires its handler, so an early click is
  // lost. Click once a second until the form's fields appear (it adds a block
  // of visible inputs; the cart page itself has at most the quantity ones).
  const visibleFields = () =>
    [...document.querySelectorAll('input, select, textarea')].filter((el) => el.offsetParent).length;
  const baseline = visibleFields();
  const started = Date.now();
  let tries = 0;
  (function attempt() {
    if (visibleFields() > baseline + 2) return;
    const button = document.querySelector('#cart-wrapper ' + COD_BUTTON);
    if (button && button.offsetParent) {
      tries += 1;
      button.click();
    }
    if (tries < 10 && Date.now() - started < 20000) setTimeout(attempt, 1000);
  })();
}


/* --------------------------------------------- simple product form (home) -- */

// cs-featured-simple: one variant select and a quantity, added through the
// same cart event as the packs so the drawer opens and refreshes.
async function onSimpleSubmit(event) {
  const form = event.target.closest ? event.target.closest('form[data-cs-simple-form]') : null;
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('[type="submit"]');
  const id = parseInt(form.elements.id.value, 10);
  const quantity = Math.max(1, parseInt(form.elements.quantity.value, 10) || 1);
  if (button) button.setAttribute('aria-busy', 'true');

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + id, quantity }],
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id, quantity }],
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    const payload = await res.json();
    if (!res.ok) throw new Error(payload.description || 'cart/add failed');
    const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'cs-featured-simple', sections: payload.sections, didError: false },
    });
  } catch (error) {
    deferred.resolve({ cart: null, detail: { didError: true, source: 'cs-featured-simple' } });
    console.warn('[cs-buybox] simple add failed:', error);
    form.submit();
  } finally {
    if (button) button.removeAttribute('aria-busy');
  }
}

/* -------------------------------------------------------------------- init -- */

let scheduled = false;
const observer = new MutationObserver(() => {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    renderAll();
  });
});

function start() {
  renderAll();
  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('change', onChange);
  document.addEventListener('click', onAddToCart, true);
  document.addEventListener('submit', onSimpleSubmit);
  document.addEventListener(
    'click',
    (event) => {
      const fallback = event.target.closest && event.target.closest('[data-cs-cod-fallback]');
      if (!fallback) onCodClick(event);
    },
    true
  );
  openCartCodForm();
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
